import { action, computed, observable } from "mobx";
import { InfiniteLoader, Table } from "react-virtualized";
import { isEqual } from "apollo-utilities";

type FetchCallback = (skip?: number, limit?: number) => Promise<any>;

interface ListHolder {
  scrollToPosition: (scrollTop: number) => void;
  scrollToRow: (index: number) => void;
}

export class InfiniteScrollSearchManager<ROW extends { _id: string; type?: string }> {
  @observable protected _isLoading = false;

  @observable protected _items: ROW[] = [];
  protected _listHolder: ListHolder;
  protected _infiniteLoader: InfiniteLoader;
  protected _scrollContainer: HTMLElement | null = null;

  constructor(private _fetch: FetchCallback, lazy = false) {
    !lazy && this.fetch();
  }

  @computed get rowCount() {
    // eslint-disable-next-line no-prototype-builtins
    if (this._items[0]?.hasOwnProperty("type")) {
      return this._items.filter((x) => x.type !== "group").length;
    }
    return this._items.length;
  }

  get items() {
    return this._items;
  }

  get isLoading() {
    return this._isLoading;
  }

  setLoading = (v: boolean) => {
    this._isLoading = v;
  };

  fetch: FetchCallback = (skip?, limit?) => {
    this._isLoading = true;

    return this._fetch(skip, limit).finally(() => {
      this._isLoading = false;
    });
  };

  loadMoreRows = () => {
    const skip = this.rowCount;
    const limit = 20;

    return this.fetch(skip, limit);
  };

  getRow = (index: number) => {
    return this._items[index];
  };

  setListHolder = (ref: Table) => {
    this._listHolder = ref;
  };

  setScrollContainer = (el: HTMLElement) => {
    this._scrollContainer = el;
  };

  @action.bound clear() {
    this.scrollTop();
    this._items = [];
  }

  scrollTop = () => {
    if (this._scrollContainer) {
      this._scrollContainer.scrollTop = 0;
    } else {
      this._listHolder && this._listHolder.scrollToPosition(0);
    }
  };

  updateRow = (index: number, val: ROW) => {
    this._items[index] = val;
  };

  prependRow = (val: ROW) => {
    this._items.unshift(val);
  };

  deleteRow = (index: number) => {
    this._items.splice(index, 1);
  };

  push = (rows: ROW[]) => this._items.push(...rows);

  merge = (rows: ROW[]) => {
    if (!isEqual(this.items, rows)) {
      this._items = rows;
    }
  };

  unshift = (row: ROW) => this._items.unshift(row);

  setInfiniteLoaderRef = (ref) => ref && (this._infiniteLoader = ref);

  getIndexById = (id: string) => {
    return this._items.findIndex((x) => x._id === id);
  };
}
